import { useEffect, useReducer, useState } from 'react';
import type { Assignment, Instructor } from '@/types';
import { ic, sized } from '@/components/Icons';
import { DataTable, PageHeader, Pagination, type Column } from '@/components/ui';
import { HoverCard, HoverRow } from '@/components/ui/HoverCard';
import { COUNTRIES, ds, userOrgIds } from '@/data';
import { useI18n } from '@/i18n';
import { paginate, sortRows, useTableState, type SortMap } from '@/hooks/useTableState';
import { useGo } from '@/hooks/useRoute';
import { useAppStore } from '@/store/useAppStore';
import { InstrAvatar } from './InstrAvatar';
import { esDeUnSite } from '@/lib/roles';

type Row = Instructor & { _key: string; _asg: Assignment[]; _disq: number };

function InstructorNameCell({ r }: { r: Row }) {
  const { lang, t } = useI18n();
  const asg = r._asg || [];
  const country = COUNTRIES.find((c) => c.code === r.Country)?.[lang === 'es' ? 'name_es' : 'name_en'] || r.Country;

  return (
    <HoverCard
      width={320}
      height={300}
      trigger={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <InstrAvatar instr={r} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{r.EnglishFirstName} {r.EnglishLastName}</div>
            <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{r.InstructorId}</div>
          </div>
        </div>
      }
    >
      {() => (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 10 }}>
            <InstrAvatar instr={r} size={44} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--brand-ink)', letterSpacing: '-0.01em' }}>
                {r.EnglishFirstName} {r.EnglishLastName}
              </div>
              <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-3)', marginTop: 2 }}>{r.InstructorId}</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
            <HoverRow label={lang === 'es' ? 'Cargo' : 'Title'}>{r.JobTitle || '—'}</HoverRow>
            <HoverRow label="Email" bad={!r.EmailAddress}>{r.EmailAddress || (lang === 'es' ? 'Sin dato' : 'Missing')}</HoverRow>
            <HoverRow label={lang === 'es' ? 'Móvil' : 'Mobile'}>{r.Mobile1 || '—'}</HoverRow>
            <HoverRow label={lang === 'es' ? 'País' : 'Country'}>{country}</HoverRow>
            <HoverRow label={lang === 'es' ? 'Certificación' : 'Certification'}>{r.Certification}</HoverRow>
            <HoverRow label="Roles">{r.AccessRoles}</HoverRow>
          </div>
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600 }}>{lang === 'es' ? 'Asignaciones' : 'Assignments'}</span>
              {asg.length === 0 ? (
                <span className="chip warn" style={{ fontSize: 9 }}>{t('instr.filter_orphan')}</span>
              ) : (
                <span className="mono" style={{ fontSize: 12, fontWeight: 700 }}>{asg.length}</span>
              )}
            </div>
            {asg.length ? (
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>
                {asg[0].SiteName}{asg.length > 1 ? ` · +${asg.length - 1}` : ''}
              </div>
            ) : null}
          </div>
        </>
      )}
    </HoverCard>
  );
}

export function InstructorsList() {
  const { lang, t } = useI18n();
  const [, recontar] = useReducer((x: number) => x + 1, 0);
  const go = useGo();
  const role = useAppStore((s) => s.role);
  const user = useAppStore((s) => s.user);

  const [q, setQ] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const table = useTableState(18);

  const countryName = (code: string) => {
    const c = COUNTRIES.find((x) => x.code === code);
    return c ? (lang === 'es' ? c.name_es : c.name_en) : code || '';
  };

  let all = ds.listInstructors({ q });
  if (role === 'PARTNER_MGR') {
    const oids = userOrgIds(user);
    const ids = ds.listAssignments().filter((a) => oids.includes(a.OrgId)).map((a) => a.InstructorId);
    all = all.filter((i) => ids.includes(i.InstructorId));
  } else if (esDeUnSite(role) && user?.siteId) {
    const ids = ds.listAssignments({ siteId: user.siteId }).map((a) => a.InstructorId);
    all = all.filter((i) => ids.includes(i.InstructorId));
  }

  // disassociated instructors are managed in Administración
  all = all.filter((i) => !i.disassociated);

  const countryOptions = Array.from(new Set(all.map((i) => i.Country).filter(Boolean)))
    .map((c) => ({ code: c, name: countryName(c) }))
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  if (countryFilter) all = all.filter((i) => i.Country === countryFilter);
  if (siteFilter) {
    const ids = ds.listAssignments({ siteId: siteFilter }).map((a) => a.InstructorId);
    all = all.filter((i) => ids.includes(i.InstructorId));
  }

  const enriched: Row[] = all.map((i) => ({
    ...i,
    _key: i.InstructorId,
    _asg: ds.listAssignments({ instructorId: i.InstructorId }),
    _disq: ds.listDisassocRequests({ instructorId: i.InstructorId }).length,
  }));

  const sortValues: SortMap<Row> = {
    name: { type: 'text', get: (r) => `${r.EnglishLastName || ''} ${r.EnglishFirstName || ''}`.trim() },
    JobTitle: { type: 'text', get: (r) => r.JobTitle || '' },
    country: { type: 'text', get: (r) => countryName(r.Country) },
    asg: { type: 'number', get: (r) => r._asg.length },
    disq: { type: 'number', get: (r) => r._disq },
    cert: { type: 'text', get: (r) => r.Certification || '' },
  };

  const { rows, page, pageCount } = paginate(sortRows(enriched, table.sort, sortValues), table.page, table.per);
  const siteOptions = ds.listSites().slice(0, 60);
  // El botón que lleva a la bandeja solo sale si hay pendientes, y las
  // pendientes llegaban únicamente con la hidratación del inicio de sesión.
  // Resultado: una solicitud enviada después de que el Admin ETC entrara no
  // le aparecía, y encima se quedaba sin forma de llegar a la pantalla.
  useEffect(() => {
    void ds.refreshRequests().then(recontar).catch(() => {
      /* si falla se sigue viendo lo que ya había */
    });
  }, []);

  const pendingRequests = ds.listDisassocRequests().length;

  const columns: Column<Row>[] = [
    {
      key: 'name',
      // La columna siempre ordenó por apellido; el encabezado no lo decía, así
      // que se buscaba una columna «Apellido» que no existe (prueba 13.3).
      label: (
        <>
          Instructor{' '}
          <span style={{ fontWeight: 400, color: 'var(--text-3)', textTransform: 'none' }}>
            {lang === 'es' ? '(ordena por apellido)' : '(sorts by last name)'}
          </span>
        </>
      ),
      sortable: true,
      render: (r) => <InstructorNameCell r={r} />,
    },
    { key: 'JobTitle', label: lang === 'es' ? 'Cargo' : 'Title', muted: true, sortable: true },
    { key: 'country', label: lang === 'es' ? 'País' : 'Country', sortable: true, render: (r) => countryName(r.Country) },
    {
      key: 'asg',
      label: lang === 'es' ? 'Asignaciones' : 'Assignments',
      sortable: true,
      render: (r) =>
        r._asg.length === 0 ? (
          <span className="chip warn">{t('instr.filter_orphan')}</span>
        ) : r._asg.length === 1 ? (
          <span style={{ fontSize: 12 }}>{r._asg[0].SiteName}</span>
        ) : (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12 }}>{r._asg[0].SiteName}</span>
            <span className="chip brand">+{r._asg.length - 1}</span>
          </div>
        ),
    },
    {
      key: 'disq',
      label: lang === 'es' ? 'Desasociar' : 'Disassociate',
      align: 'center',
      sortable: true,
      render: (r) =>
        r._disq > 0 ? (
          <button
            className="chip err"
            onClick={(e) => {
              e.stopPropagation();
              go('instructors', { view: 'disassoc' });
            }}
            data-tip={lang === 'es' ? `${r._disq} solicitud(es) — clic para gestionar` : `${r._disq} request(s) — click to manage`}
            style={{ fontWeight: 700, cursor: 'pointer' }}
          >
            {ic.warn} {r._disq}
          </button>
        ) : (
          <span style={{ color: 'var(--text-3)' }}>—</span>
        ),
    },
    { key: 'cert', label: 'Cert.', sortable: true, render: (r) => <span className="chip">{r.Certification}</span> },
    { key: 'go', label: '', align: 'right', render: () => <span style={{ color: 'var(--text-3)' }}>{ic.chevR}</span> },
  ];

  return (
    <div className="screen-enter" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <PageHeader
        title={t('nav.instructors')}
        sub={lang === 'es' ? `${all.length} instructores en tu alcance` : `${all.length} instructors in scope`}
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {pendingRequests > 0 ? (
              <button className="btn sm" onClick={() => go('instructors', { view: 'disassoc' })} style={{ color: 'var(--err)' }}>
                {sized(ic.warn, 13)} {lang === 'es' ? 'Solicitudes de desasociación' : 'Disassociation requests'} ({pendingRequests})
              </button>
            ) : null}
            <select
              value={countryFilter}
              onChange={(e) => {
                setCountryFilter(e.target.value);
                table.setPage(1);
              }}
              aria-label={lang === 'es' ? 'Filtrar por país' : 'Filter by country'}
              style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid var(--line-2)', fontSize: 12, background: 'var(--surface)', maxWidth: 170 }}
            >
              <option value="">{lang === 'es' ? 'Todos los países' : 'All countries'}</option>
              {countryOptions.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
            <select
              value={siteFilter}
              onChange={(e) => {
                setSiteFilter(e.target.value);
                table.setPage(1);
              }}
              aria-label={t('instr.filter_by_site')}
              style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid var(--line-2)', fontSize: 12, background: 'var(--surface)', maxWidth: 200 }}
            >
              <option value="">{t('instr.filter_by_site')}</option>
              {siteOptions.map((s) => (
                <option key={s.SiteId} value={s.SiteId}>{s.SiteName}</option>
              ))}
            </select>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }}>{ic.search}</span>
              <input
                placeholder={t('common.search')}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  table.setPage(1);
                }}
                style={{ padding: '8px 12px 8px 32px', border: '1px solid var(--line-2)', borderRadius: 7, fontSize: 13, width: 200 }}
              />
            </div>
          </div>
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        sort={table.sort}
        onSort={table.onSort}
        stickyHeader
        onRowClick={(i) => go('instructorDetail', { instructorId: i.InstructorId })}
      />
      <Pagination page={page} pageCount={pageCount} onPage={table.setPage} total={all.length} per={table.per} onPer={table.onPer} perOptions={[10, 18, 25, 50, 100]} />
    </div>
  );
}
